package com.appsyde.shared.app.presentation.inbox

import com.appsyde.shared.modules.ai.domain.model.AiExtractV2
import com.appsyde.shared.modules.ai.domain.usecase.GenerateAiExtractUseCase
import com.appsyde.shared.modules.ai.domain.usecase.PreloadAiExtractsUseCase
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Deferred
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

/**
 * Shared orchestrator for AI extracts across list/respond flows.
 *
 * Goals:
 * - deduplicate concurrent generate calls by messageId
 * - share extract cache by ticketId across view models
 * - keep backend contention (409 RUNNING) minimal
 */
class ExtractOrchestrator(
    private val generateAiExtractUseCase: GenerateAiExtractUseCase,
    private val preloadAiExtractsUseCase: PreloadAiExtractsUseCase,
) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val inFlightMutex = Mutex()
    private val inFlightByMessageId = mutableMapOf<String, Deferred<AiExtractV2>>()

    private val _extractByTicketId = MutableStateFlow<Map<String, AiExtractV2>>(emptyMap())
    val extractByTicketId: StateFlow<Map<String, AiExtractV2>> = _extractByTicketId.asStateFlow()

    fun snapshotExtracts(): Map<String, AiExtractV2> = _extractByTicketId.value

    fun getCachedExtract(ticketId: String): AiExtractV2? = _extractByTicketId.value[ticketId]

    fun putExtract(ticketId: String, extract: AiExtractV2) {
        _extractByTicketId.update { it + (ticketId to extract) }
    }

    /** Invalidate cached extract for a ticket (e.g. after sending a reply so next load regenerates). */
    fun invalidateExtract(ticketId: String) {
        _extractByTicketId.update { it - ticketId }
    }

    suspend fun preload(messageIds: List<String>, force: Boolean = false) {
        if (messageIds.isEmpty()) return
        preloadAiExtractsUseCase(messageIds = messageIds, force = force)
    }

    suspend fun ensureExtract(
        ticketId: String,
        messageId: String,
        force: Boolean = false,
    ): AiExtractV2 {
        if (!force) {
            getCachedExtract(ticketId)?.let { return it }
        }

        val deferred = inFlightMutex.withLock {
            inFlightByMessageId[messageId] ?: scope.async {
                try {
                    val extract = generateAiExtractUseCase(messageId = messageId, force = force).extract
                    putExtract(ticketId, extract)
                    extract
                } finally {
                    inFlightMutex.withLock {
                        inFlightByMessageId.remove(messageId)
                    }
                }
            }.also {
                inFlightByMessageId[messageId] = it
            }
        }

        val extract = deferred.await()
        // Ensure ticketId points to latest known extract for this message.
        putExtract(ticketId, extract)
        return extract
    }
}
